import {
  durations,
  easings,
  borderRadius,
  colors,
  shadows,
  spacing,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  type Colors,
  type Shadows,
  type Durations,
  type Easings,
  type BorderRadius,
  type Spacing,
  type Fonts,
  type FontSizes,
  type FontWeights,
  type LineHeights,
} from "@indeed/ifl-tokens/react-native";
import { colors as colorsDark } from "@indeed/ifl-tokens/react-native/dark";
import React, { useMemo } from "react";
import { StyleSheet } from "react-native";

export interface Theme {
  colors: Colors;
  shadows: Shadows;
  durations: Durations;
  easings: Easings;
  borderRadius: BorderRadius;
  spacing: Spacing;
  fonts: Fonts;
  fontSizes: FontSizes;
  fontWeights: FontWeights;
  lineHeights: LineHeights;
}

type ColorMode = "light" | "dark";

const defaultTheme: Theme = {
  colors,
  shadows,
  durations,
  easings,
  borderRadius,
  spacing,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
};

interface ThemeContext {
  theme: Theme;
}

const ThemeContext = React.createContext<ThemeContext>({
  theme: defaultTheme,
});

export interface ThemeProviderProps {
  children: React.ReactNode;
  colorMode?: ColorMode;
}

export const ThemeProvider = ({ children, colorMode = "light" }: ThemeProviderProps) => {
  const theme = colorMode === "light" ? defaultTheme : { ...defaultTheme, colors: colorsDark };

  return <ThemeContext.Provider value={{ theme }}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = React.useContext(ThemeContext);

  return context.theme;
};

export const useThemedStylesheet = <T extends StyleSheet.NamedStyles<T>>(
  factory: (theme: Theme) => T,
): T => {
  const theme = useTheme();
  return useMemo(() => StyleSheet.create(factory(theme)), [theme, factory]);
};
